/**
 * Transaction controls for the query toolbar.
 * Renders mode toggle (Auto/Manual/Smart), isolation level, Commit, and Rollback buttons.
 */

import { Check, Undo2, ChevronDown, ShieldCheck } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components/ui/popover';
import { useState } from 'react';
import type { TransactionMode, TransactionState, TransactionIsolationLevel } from '../../core/types/workspaceTypes';
import type { IsolationLevelDropdownState } from '../hooks/useTransactionControl';
import { computeTransactionEnergyState } from '../utils/transactionIndicator';
import { useShortcutLabel } from '../../../keybindings/hooks/useShortcutLabel';

const MODE_LABELS: Record<TransactionMode, string> = {
  auto: 'Auto',
  manual: 'Manual',
  smart: 'Smart',
};

const MODE_DESCRIPTIONS: Record<TransactionMode, string> = {
  auto: 'Each query auto-commits immediately',
  manual: 'You control when to commit or rollback',
  smart: 'Auto-detect DML and switch to manual',
};

const ISOLATION_LEVELS: { value: TransactionIsolationLevel; label: string; description: string }[] = [
  { value: 'default', label: 'Default', description: 'Use the engine default isolation level' },
  { value: 'read-uncommitted', label: 'Read Uncommitted', description: 'Allows dirty reads' },
  { value: 'read-committed', label: 'Read Committed', description: 'Prevents dirty reads' },
  { value: 'repeatable-read', label: 'Repeatable Read', description: 'Prevents non-repeatable reads' },
  { value: 'serializable', label: 'Serializable', description: 'Strictest isolation level' },
];

const ISOLATION_SHORT_LABELS: Record<TransactionIsolationLevel, string> = {
  'default': 'Default',
  'read-uncommitted': 'RU',
  'read-committed': 'RC',
  'repeatable-read': 'RR',
  'serializable': 'S',
};

interface TransactionToolbarControlsProps {
  transactionMode: TransactionMode;
  transactionState: TransactionState;
  isolationLevel: TransactionIsolationLevel;
  onModeChange: (mode: TransactionMode) => void;
  onIsolationLevelChange: (level: TransactionIsolationLevel) => void;
  onCommit: () => void;
  onRollback: () => void;
  commitEnabled: boolean;
  rollbackEnabled: boolean;
  modeChangeDisabled: boolean;
  isolationLevelState: IsolationLevelDropdownState;
  disabled?: boolean;
}

export function TransactionToolbarControls({
  transactionMode,
  transactionState,
  isolationLevel,
  onModeChange,
  onIsolationLevelChange,
  onCommit,
  onRollback,
  commitEnabled,
  rollbackEnabled,
  modeChangeDisabled,
  isolationLevelState,
  disabled = false,
}: TransactionToolbarControlsProps) {
  const commitShortcutLabel = useShortcutLabel('query.commitTransaction');
  const rollbackShortcutLabel = useShortcutLabel('query.rollbackTransaction');
  const [isModeMenuOpen, setIsModeMenuOpen] = useState(false);
  const [isIsolationMenuOpen, setIsIsolationMenuOpen] = useState(false);

  const showCommitRollback = transactionMode !== 'auto';
  const showIsolationOnFirstRow = isolationLevelState.visible;
  const showTransactionStartHint =
    showCommitRollback &&
    transactionState === 'idle' &&
    !commitEnabled &&
    !rollbackEnabled;

  // Resting is a neutral ghost (CH-09 §6 item 4) — the always-amber `Tx:
  // Auto` control was the flagship anti-pattern this rule exists to fix.
  // Tint only when there is genuinely something uncommitted: manual mode
  // declares that intent even before a transaction opens, or a transaction
  // is already open (active/error). See computeTransactionEnergyState.
  const isEnergized = computeTransactionEnergyState(transactionMode, transactionState) === 'energy';
  const transactionGroupTintClass = isEnergized ? 'border-energy bg-energy-soft' : 'border-border';
  const transactionGroupDividerClass = isEnergized ? 'border-energy/30' : 'border-border';
  // Hover/focus deliberately stay on the button's own neutral ghost defaults
  // in both states — orange is forbidden on hover states and focus rings
  // (CH-09 §6 item 6), so no color override here at all.
  // Corners: `rounded-none` only makes sense flush inside the bordered
  // group's own `overflow-hidden` clip (Manual/Smart). Standalone in Auto
  // (no wrapper) the button needs its ordinary `rounded-md` back, or it
  // would sit square-cornered next to Plan/Result/Recent's rounded ghost
  // buttons — the opposite of reading as one of them.
  const transactionButtonClass = `border-0 bg-transparent shadow-none ${showCommitRollback ? 'rounded-none' : 'rounded-md'}`;
  const transactionLabelClass = isEnergized ? 'text-energy-text' : 'text-muted-foreground';
  // G-5 (T6.1b): the wrapping border/rounded-box exists to bind the mode row
  // to the Commit/Rollback row beneath it. In Auto there is no second row
  // (`showCommitRollback` is false), so the border was binding a single
  // ghost control — the only bordered control on the whole query toolbar,
  // sitting immediately after Execute while Plan/Result/Recent are all
  // borderless ghosts beside it. Drop it there; Auto now reads as a ghost
  // among ghosts, the same weight as its neighbors. Manual/Smart keep it —
  // there it's doing real work, binding two rows together, and the tint
  // is genuinely earned (something is uncommitted).
  const transactionGroupWrapperClass = showCommitRollback
    ? `rounded-md border ${transactionGroupTintClass}`
    : '';

  return (
    <div className="flex items-start gap-1">
      <div className="h-8 w-px bg-border" data-toolbar-item />

      <div className={`flex flex-col overflow-hidden ${transactionGroupWrapperClass}`}>
        <div className={`flex ${showCommitRollback ? `border-b ${transactionGroupDividerClass}` : ''}`}>
          {/* Transaction mode toggle sits on first row */}
          <Popover open={isModeMenuOpen} onOpenChange={setIsModeMenuOpen}>
            <PopoverTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                disabled={disabled}
                title="Transaction commit mode"
                data-testid="transaction-mode-button"
                data-toolbar-item
                className={`${transactionButtonClass} px-2 ${showIsolationOnFirstRow ? `border-r ${transactionGroupDividerClass}` : ''}`}
              >
                <span className={`font-mono text-[11px] font-semibold tracking-wider ${transactionLabelClass}`}>
                  TX
                </span>
                {transactionMode !== 'auto' && (
                  <span className="ml-0.5">{MODE_LABELS[transactionMode]}</span>
                )}
                {/* G-5 (T6.1b): gated explicitly on `transactionMode !==
                    'auto'`, not on `isEnergized` — auto-commit never leaves
                    a lingering open transaction to act on, so this dot must
                    never show there even if some producer reports `active`
                    while mode is still `auto` (verified reachable via the
                    MCP `transaction_begin` tool — see task-T6.1b-report.md).
                    `animate-pulse` makes "genuinely open" (this dot) clearly
                    out-read "armed" (manual/smart idle — tinted border only,
                    no dot at all): the tint alone already spent the orange
                    budget before anything opened, so the state change that
                    matters most needs its own, stronger signal rather than
                    a fourth indicator. */}
                {transactionMode !== 'auto' && transactionState === 'active' && (
                  <span className="ml-1 h-2 w-2 rounded-full bg-energy animate-pulse inline-block" />
                )}
                {transactionMode !== 'auto' && transactionState === 'error' && (
                  <span className="ml-1 h-2 w-2 rounded-full bg-destructive inline-block" />
                )}
                <ChevronDown className="h-3 w-3" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-64 p-1">
              {(['auto', 'manual', 'smart'] as TransactionMode[]).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  className="w-full flex items-start gap-2 rounded-sm px-2 py-2 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                  onClick={() => {
                    onModeChange(mode);
                    setIsModeMenuOpen(false);
                  }}
                  disabled={modeChangeDisabled && mode !== transactionMode}
                  data-testid={`transaction-mode-${mode}`}
                >
                  <div className="flex flex-col flex-1">
                    <div className="text-sm font-medium leading-5">{MODE_LABELS[mode]}</div>
                    <div className="text-xs text-muted-foreground leading-4">{MODE_DESCRIPTIONS[mode]}</div>
                  </div>
                  {transactionMode === mode && <Check className="h-4 w-4 mt-0.5 text-primary" />}
                </button>
              ))}
            </PopoverContent>
          </Popover>

          {/* Isolation selector sits on first row, to the right of mode */}
          {showIsolationOnFirstRow && (
            <Popover open={isIsolationMenuOpen} onOpenChange={setIsIsolationMenuOpen}>
              <PopoverTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={isolationLevelState.disabled}
                  title={`Isolation level: ${ISOLATION_LEVELS.find((l) => l.value === isolationLevel)?.label ?? 'Default'}`}
                  data-testid="transaction-isolation-button"
                  data-toolbar-item
                  className={`${transactionButtonClass} px-2`}
                >
                  <ShieldCheck className="h-3.5 w-3.5" />
                  {ISOLATION_SHORT_LABELS[isolationLevel]}
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-72 p-1">
                {ISOLATION_LEVELS.map((level) => (
                  <button
                    key={level.value}
                    type="button"
                    className="w-full flex items-start gap-2 rounded-sm px-2 py-2 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={() => {
                      onIsolationLevelChange(level.value);
                      setIsIsolationMenuOpen(false);
                    }}
                    data-testid={`transaction-isolation-${level.value}`}
                  >
                    <div className="flex flex-col flex-1">
                      <div className="text-sm font-medium leading-5">{level.label}</div>
                      <div className="text-xs text-muted-foreground leading-4">{level.description}</div>
                    </div>
                    {isolationLevel === level.value && <Check className="h-4 w-4 mt-0.5 text-primary" />}
                  </button>
                ))}
              </PopoverContent>
            </Popover>
          )}
        </div>

        {/* Commit/Rollback row (or pre-transaction helper message) */}
        {showCommitRollback && (
          <div className="flex">
            {showTransactionStartHint ? (
              <div
                className="px-2 py-1 text-[10px] text-muted-foreground/90 whitespace-nowrap"
                data-testid="transaction-start-hint"
                data-toolbar-item
                title="Run a query to begin a transaction"
              >
                Run a query to start a transaction
              </div>
            ) : (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onCommit}
                  disabled={!commitEnabled}
                  title={`Commit transaction${commitShortcutLabel ? ` (${commitShortcutLabel})` : ''}`}
                  data-testid="transaction-commit-button"
                  data-toolbar-item
                  className={`${transactionButtonClass} flex-1 basis-0 justify-center px-2 border-r ${transactionGroupDividerClass}`}
                >
                  <Check className="h-4 w-4" />
                  Commit
                </Button>

                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onRollback}
                  disabled={!rollbackEnabled}
                  title={transactionState === 'error'
                    ? `The transaction is in error and can only be rolled back${rollbackShortcutLabel ? ` (${rollbackShortcutLabel})` : ''}`
                    : `Rollback transaction${rollbackShortcutLabel ? ` (${rollbackShortcutLabel})` : ''}`}
                  data-testid="transaction-rollback-button"
                  data-attention={transactionState === 'error' ? 'true' : undefined}
                  data-toolbar-item
                  className={`${transactionButtonClass} flex-1 basis-0 justify-center px-2${transactionState === 'error' ? ' text-destructive font-semibold' : ''}`}
                >
                  <Undo2 className="h-4 w-4" />
                  Rollback
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
